import { useTranslation } from 'react-i18next';

import { type AddressProps, days } from '@myphx-engineering/phx-design-system';
import { PharmacyInfo } from '@phx/myphx-lib';

import { getFragment } from '../../graphql';
import {
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
} from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';
import { usePatientContext } from '../../hooks/use-patient-context';
import { titleCase } from '../../util';
import { getDeliveryTimeDisplay } from '../../util/pharmacy/get-delivery-time-display';
import { mapWeeklyHours } from '../../util/pharmacy/map-weekly-hours';

type PharmacyInfoProps = {
    location: PharmacyProviderInfoFragment | MailOrderProviderInfoFragment;
    distance?: number | null;
    showFavorite?: boolean;
    shippingMethod?: string | null;
};

export const LocationInfo = ({
    location,
    distance,
    showFavorite = true,
    shippingMethod,
}: PharmacyInfoProps) => {
    const { t } = useTranslation();
    const { costPlusDrugsEnabled } = useFlags();
    const { preferences } = usePatientContext();
    const { favoritePharmacies } = preferences;
    const { checkIsFavorite, toggleFavorite } = favoritePharmacies;
    let locationInfo;
    const day = days[new Date().getDay()];

    let deliveryTime;
    if (
        location.__typename === 'MailOrderProvider' &&
        shippingMethod !== null
    ) {
        deliveryTime = costPlusDrugsEnabled
            ? getDeliveryTimeDisplay(location, shippingMethod ?? undefined)
            : location.deliveryTimeDisplay;
    }

    switch (location.__typename) {
        case 'PharmacyProvider': {
            const weeklyHours = location?.weeklyHoursByDayOfWeek
                ? mapWeeklyHours(getFragment(location.weeklyHoursByDayOfWeek))
                : undefined;

            locationInfo = {
                name: titleCase(location?.name),
                address: location?.address as AddressProps['address'],
                phone: location?.phoneNumber,
                hours: weeklyHours?.[day],
                day: t('locationInfo.today'),
                weeklyHours,
                distance: distance
                    ? {
                          distance: distance,
                          units: t('common.distance.distanceUnit'),
                      }
                    : undefined,
                favorite: showFavorite
                    ? checkIsFavorite(location.id)
                    : undefined,
                toggleFavorite: showFavorite
                    ? () => toggleFavorite(location.id)
                    : undefined,
            };
            break;
        }
        case 'MailOrderProvider':
            locationInfo = {
                name: titleCase(location?.name),
                phone: location.phoneNumber,
                shippingTime: deliveryTime ?? undefined,
                url: location?.url ?? undefined,
                favorite: showFavorite
                    ? checkIsFavorite(location.id)
                    : undefined,
                toggleFavorite: showFavorite
                    ? () => toggleFavorite(location.id)
                    : undefined,
            };
            break;
        default:
            locationInfo = {
                name: titleCase(location?.name),
                phone: location?.phoneNumber,
            };
            break;
    }

    return <PharmacyInfo {...locationInfo} />;
};
